'use client'
import type { ForwardedRef, MutableRefObject } from 'react'
import { useMemo } from 'react'
import CreatableSelect, { type CreatableProps } from 'react-select/creatable'
import Select, { type GroupBase, type SelectInstance } from 'react-select'
import { forwardRef } from '@chakra-ui/react'

export interface GrandSelectSimpleOption<Value> {
  label: string
  value: Value
}

export type GrandSelectGroup<Option> = GroupBase<Option>

export type GrandSelectElement<Option = unknown, IsMulti extends boolean = false> = SelectInstance<
  Option,
  IsMulti
>
export type GrandSelectRef<Option = unknown, IsMulti extends boolean = false> = MutableRefObject<
  GrandSelectElement<Option, IsMulti>
>

export type GrandSelectProps<Option = unknown, IsMulti extends boolean = false> = CreatableProps<
  Option,
  IsMulti,
  GroupBase<Option>
> & { isCreatable?: boolean }

const GrandSelectInner = <Option extends unknown = unknown, IsMulti extends boolean = false>(
  { styles = {}, isCreatable, ...props }: GrandSelectProps<Option, IsMulti>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>
) => {
  const {
    control,
    menu,
    multiValue,
    multiValueRemove,
    multiValueLabel,
    menuPortal,
    ...restStyles
  } = styles

  const SelectComponent = useMemo(() => (isCreatable ? CreatableSelect : Select), [isCreatable])

  return (
    <SelectComponent<Option, IsMulti>
      formatCreateLabel={(inputValue) => `Skapa ny "${inputValue}"`}
      placeholder="Välj eller ange..."
      ref={ref}
      styles={{
        control: (baseStyles, state) => {
          const focusColor = 'var(--chakra-colors-input-focus, var(--chakra-colors-blue-500))'
          const borderColor = 'var(--chakra-colors-chakra-border-color)'
          const borderColorHover = 'var(--chakra-colors-gray-300)'

          return {
            ...baseStyles,
            minHeight: '40px',
            minWidth: '150px',
            width: '100%',
            borderColor: state.isFocused ? focusColor : borderColor,
            borderRadius: 'var(--chakra-radii-lg)',
            ...(state.isFocused && { boxShadow: `0 0 0 1px ${focusColor}` }),
            '&:hover': {
              borderColor: state.isFocused ? focusColor : borderColorHover
            },
            ...control?.(baseStyles, state)
          }
        },
        menu: (baseStyles, state) => ({
          ...baseStyles,
          borderRadius: 'var(--chakra-radii-lg)',
          overflow: 'hidden',
          zIndex: 'var(--chakra-zIndices-dropdown)',
          ...menu?.(baseStyles, state)
        }),
        menuPortal: (baseStyles, state) => ({
          ...baseStyles,
          zIndex: 'var(--chakra-zIndices-dropdown)',
          ...menuPortal?.(baseStyles, state)
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
          paddingLeft: 'var(--chakra-space-4)'
        }),
        multiValue: (baseStyles, state) => ({
          ...baseStyles,
          borderRadius: 'var(--chakra-radii-xl)',
          ...multiValue?.(baseStyles, state)
        }),
        multiValueRemove: (baseStyles, state) => ({
          ...baseStyles,
          borderRadius: '0 var(--chakra-radii-lg) var(--chakra-radii-lg) 0',
          ...multiValueRemove?.(baseStyles, state)
        }),
        multiValueLabel: (baseStyles, state) => ({
          ...baseStyles,
          paddingLeft: '8px',
          ...multiValueLabel?.(baseStyles, state)
        }),
        ...restStyles
      }}
      {...props}
    />
  )
}

export const GrandSelect = forwardRef(GrandSelectInner) as <
  Option = unknown,
  IsMulti extends boolean = false
>(
  props: GrandSelectProps<Option, IsMulti> & {
    ref?: ForwardedRef<SelectInstance<Option, IsMulti>>
  }
) => ReturnType<typeof GrandSelectInner>
